import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';

function IdCardIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" className="add-emp-input-icon">
      <path
        fill="currentColor"
        d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM6 10h2v2H6v-2zm0 4h8v2H6v-2zm0-8h8v2H6V6zm10 0v8l4-4-4-4z"
      />
    </svg>
  );
}

function PhoneSmallIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
      />
    </svg>
  );
}

function MailSmallIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
      />
    </svg>
  );
}

function GroupSmallIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
      />
    </svg>
  );
}

function PersonPlaceholderIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

function ResultAvatar({ src, name }) {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return <img className="add-emp-result-avatar-img" src={src} alt="" onError={() => setBroken(true)} />;
  }
  return (
    <span className="add-emp-result-avatar-fallback" aria-hidden="true">
      <PersonPlaceholderIcon />
    </span>
  );
}

function AddEmployeePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('phone');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [preview, setPreview] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const onPhoneChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneDigits(v);
  };

  const runSearch = async (e) => {
    e.preventDefault();
    setSearchError('');
    setAddError('');
    setPreview(null);
    setSearching(true);
    try {
      const payload =
        tab === 'phone'
          ? { phoneNumber: phoneDigits }
          : { employeeCode: codeInput.trim().toUpperCase() };
      const data = await webApi.searchEmployee(payload);
      setPreview(data);
    } catch (err) {
      setSearchError(err.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const canSearch =
    tab === 'phone' ? phoneDigits.replace(/\D/g, '').length >= 10 : codeInput.trim().length >= 3;

  const linkState = preview?.linkState;
  const emp = preview?.employee;

  const bottomLabel =
    linkState === 'already_linked'
      ? 'Already Linked'
      : linkState === 'other_employer'
        ? 'With another business'
        : 'Add Employee';

  const bottomDisabled = !preview || linkState !== 'available' || adding;

  const runAdd = async () => {
    if (!preview || linkState !== 'available') return;
    setAdding(true);
    setAddError('');
    try {
      if (tab === 'phone') {
        await webApi.linkEmployeeByPhone({ phoneNumber: phoneDigits });
      } else {
        await webApi.linkEmployeeByCode({ employeeCode: emp.employeeCode || codeInput.trim().toUpperCase() });
      }
      navigate('/employees');
    } catch (err) {
      setAddError(err.message || 'Could not add employee');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="add-emp-page">
      <header className="add-emp-topbar">
        <button type="button" className="add-emp-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="add-emp-topbar-title">Add Employee</h1>
        <span className="add-emp-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="add-emp-body">
        <div className="add-emp-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'phone'}
            className={`add-emp-tab${tab === 'phone' ? ' add-emp-tab--active' : ''}`}
            onClick={() => {
              setTab('phone');
              setPreview(null);
              setSearchError('');
              setAddError('');
            }}
          >
            Phone Number
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'code'}
            className={`add-emp-tab${tab === 'code' ? ' add-emp-tab--active' : ''}`}
            onClick={() => {
              setTab('code');
              setPreview(null);
              setSearchError('');
              setAddError('');
            }}
          >
            Employee ID
          </button>
        </div>

        <form onSubmit={runSearch} className="add-emp-form">
          {tab === 'phone' ? (
            <div className="add-emp-phone-field">
              <span className="add-emp-cc">+91</span>
              <span className="add-emp-cc-divider" aria-hidden="true" />
              <input
                className="add-emp-phone-input"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="Enter your Phone Number"
                value={phoneDigits}
                onChange={onPhoneChange}
              />
            </div>
          ) : (
            <div className="add-emp-code-field">
              <IdCardIcon />
              <input
                className="add-emp-code-input"
                type="text"
                autoCapitalize="characters"
                autoCorrect="off"
                placeholder="Enter Employee ID"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              />
            </div>
          )}

          {searchError ? (
            <p className="add-emp-inline-error" role="alert">
              {searchError}
            </p>
          ) : null}

          <button type="submit" className="add-emp-search-btn" disabled={!canSearch || searching}>
            {searching ? 'Searching…' : 'Search Employee'}
          </button>
        </form>

        {emp ? (
          <article className="add-emp-result-card">
            <div className="add-emp-result-head">
              <ResultAvatar src={emp.profilePictureUrl} name={emp.name} />
              <div className="add-emp-result-head-text">
                <p className="add-emp-result-name">{emp.name || '—'}</p>
                <p className="add-emp-result-code">{emp.employeeCode || '—'}</p>
                <div className="add-emp-badges">
                  {(emp.status || '').toLowerCase() === 'active' ? (
                    <span className="add-emp-badge add-emp-badge--active">Active</span>
                  ) : (
                    <span className="add-emp-badge add-emp-badge--muted">{emp.status || 'Inactive'}</span>
                  )}
                  {linkState === 'already_linked' ? (
                    <span className="add-emp-badge add-emp-badge--linked">Already Linked</span>
                  ) : null}
                  {linkState === 'other_employer' ? (
                    <span className="add-emp-badge add-emp-badge--other">Other business</span>
                  ) : null}
                </div>
              </div>
            </div>

            <section className="add-emp-result-section">
              <h3 className="add-emp-result-section-title">Contact Information</h3>
              <div className="add-emp-contact-row">
                <PhoneSmallIcon />
                <span>{emp.phone || '—'}</span>
              </div>
              <div className="add-emp-contact-row">
                <MailSmallIcon />
                <span>{emp.email || '—'}</span>
              </div>
            </section>

            <section className="add-emp-result-section">
              <h3 className="add-emp-result-section-title">Additional Information</h3>
              <div className="add-emp-contact-row">
                <GroupSmallIcon />
                <span>{emp.designation || '—'}</span>
              </div>
            </section>
          </article>
        ) : null}

        {addError ? (
          <p className="add-emp-inline-error add-emp-inline-error--card" role="alert">
            {addError}
          </p>
        ) : null}
      </div>

      <div className="add-emp-bottom">
        <button
          type="button"
          className={`add-emp-primary-btn${bottomDisabled ? ' add-emp-primary-btn--disabled' : ''}`}
          disabled={bottomDisabled}
          onClick={runAdd}
        >
          {adding ? 'Adding…' : bottomLabel}
        </button>
      </div>
    </div>
  );
}

export default AddEmployeePage;
